import React from "react";
import { motion } from "framer-motion";
import FarmStatusBanner from "./FarmStatusBanner";
import FieldsSummaryWidget from "./FieldsSummaryWidget";
import ClimateWidget from "./ClimateWidget";
import TeamWidget from "./TeamWidget";
import IrrigationFertilizationWidget from "./IrrigationFertilizationWidget";
import SatelliteMapPanel from "./SatelliteMapPanel";
import {
  buildPlatformLoginUrl,
  buildPublicFarmPreviewUrl,
} from "../../../utils/publicFarmPreview";

/**
 * PublicFarmPreview — Split-screen Dashboard Layout
 *
 * Left panel: Farm status banner + four key widgets stacked.
 * Right panel: Satellite map view with NDVI history selector (sticky on desktop).
 *
 * @param {object}  farm        – Farm object (name, region, size, coords, preview, ndviSnapshots, etc.)
 * @param {object}  [preview]   – Preview data override. Falls back to farm.preview.
 * @param {boolean} [isArabic]  – RTL / Arabic mode
 * @param {boolean} [showCta]   – Show the bottom CTA strip (default: true)
 */

const PublicFarmPreview = ({ farm, preview: previewProp, isArabic, showCta = true }) => {
  const preview = previewProp || farm.preview || {};
  const ndviSnapshots = preview.ndviSnapshots || farm.ndviSnapshots || [];
  const hasMap = ndviSnapshots.length > 0;

  return (
    <div className="rounded-[2rem] border border-emerald-100 bg-[#f8fcf8] shadow-[0_24px_90px_rgba(15,23,42,0.06)] overflow-hidden">
      {/* Top bar with platform link */}
      <div className={`border-b border-emerald-100 bg-white px-5 py-3 flex items-center justify-between ${isArabic ? "flex-row-reverse" : ""}`}>
        <div className={`flex items-center gap-2 ${isArabic ? "flex-row-reverse" : ""}`}>
          <span className="w-2 h-2 rounded-full bg-emerald-500" />
          <span className="text-xs font-bold text-slate-600">
            {isArabic ? "لوحة معلومات المزرعة" : "Farm Dashboard"}
          </span>
        </div>
        <a
          href={buildPublicFarmPreviewUrl(
            farm.publicPreviewId || "qLt08dSJRJMrZkZ5Jb71"
          )}
          target="_blank"
          rel="noopener noreferrer"
          className="rounded-full px-4 py-2 text-xs font-bold bg-slate-900 text-white no-underline hover:bg-slate-800 transition-colors"
        >
          {isArabic ? "افتح في المنصة ↗" : "Open in Platform ↗"}
        </a>
      </div>

      {/* Split-screen content area */}
      <div className={`flex flex-col lg:flex-row ${isArabic ? "lg:flex-row-reverse" : ""}`}>
        {/* LEFT PANEL — Widgets */}
        <div className={`flex-1 min-w-0 p-4 md:p-6 space-y-5 ${hasMap ? "lg:w-[58%] lg:max-w-[58%]" : ""}`}>
          {/* Farm Status Banner */}
          <FarmStatusBanner farm={farm} isArabic={isArabic} />

          {/* Widget grid — stacks 2-col on large if no map, single-col with map */}
          <div className={`grid gap-4 ${hasMap ? "grid-cols-1" : "grid-cols-1 lg:grid-cols-2"}`}>
            {preview.fields && preview.fields.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                <FieldsSummaryWidget fields={preview.fields} isArabic={isArabic} />
              </motion.div>
            )}

            {preview.climate && (
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <ClimateWidget climate={preview.climate} isArabic={isArabic} />
              </motion.div>
            )}

            {preview.team && (
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                <TeamWidget team={preview.team} isArabic={isArabic} />
              </motion.div>
            )}

            {(preview.irrigation || preview.fertilization) && (
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
              >
                <IrrigationFertilizationWidget
                  irrigation={preview.irrigation}
                  fertilization={preview.fertilization}
                  isArabic={isArabic}
                />
              </motion.div>
            )}
          </div>
        </div>

        {/* RIGHT PANEL — Satellite Map with History Selector */}
        {hasMap && (
          <div className="lg:w-[42%] lg:max-w-[42%] p-4 md:p-6 lg:pl-0 lg:border-l border-emerald-900/5">
            <div className="lg:sticky lg:top-6">
              <motion.div
                initial={{ opacity: 0, x: isArabic ? -24 : 24 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="h-full"
              >
                <SatelliteMapPanel
                  ndviSnapshots={ndviSnapshots}
                  farm={farm}
                  isArabic={isArabic}
                />
              </motion.div>
            </div>
          </div>
        )}
      </div>

      {/* Platform CTA strip */}
      {showCta && (
        <div className={`bg-gradient-to-r from-emerald-600 to-emerald-700 px-5 py-4 flex flex-col md:flex-row items-center justify-between gap-3 ${isArabic ? "md:flex-row-reverse" : ""}`}>
          <p className="text-sm text-emerald-50 font-medium mb-0">
            {isArabic
              ? "🔓 أنشئ حساباً مجانياً للوصول إلى البيانات الكاملة وصور الأقمار الصناعية التاريخية والتحليلات التفصيلية"
              : "🔓 Create a free account to access full data, historical satellite imagery, and detailed crop analytics"}
          </p>
          <a
            href={buildPlatformLoginUrl()}
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-full bg-white px-6 py-2.5 text-sm font-bold text-emerald-700 no-underline hover:bg-slate-50 transition-colors whitespace-nowrap shrink-0"
          >
            {isArabic ? "ابدأ مجاناً" : "Start Free →"}
          </a>
        </div>
      )}
    </div>
  );
};

export default PublicFarmPreview;
